/* Truncated collection description
   Pairs with snippets/de-truncated-collection-description.liquid and
   assets/de-collection-description.css. Loaded from snippets/de-customisations.liquid,
   gated to collection pages.

   Custom element instead of a DOMContentLoaded listener (see the inline script it
   replaces in sections/main-collection.liquid): it re-initialises when the theme editor
   re-renders the section and survives instant-page navigation, neither of which a
   DOMContentLoaded listener does.

   de-customisations.liquid renders after the theme.js <script> tag in layout/theme.liquid
   and this file is deferred like that one, so document order puts execution after theme.js
   and BaseElement is already in scope.

   The two --description-max-lines* properties are written inline by the snippet rather than
   here: they are known at render time, and setting them from a deferred script would clamp
   at the CSS fallback for the first paint and then jump. */
if (!customElements.get('truncated-text')) {
  customElements.define('truncated-text', class extends BaseElement {
    connectedCallback() {
      super.connectedCallback();

      this.description = this.querySelector('.collection__description');
      this.button = this.querySelector('.read-more-btn');
      if (!this.description || !this.button) return;

      this.measure();

      this.on(window, 'resize', theme.utils.debounce(this.measure.bind(this), 100));
      document.fonts.ready.then(this.measure.bind(this));
      this.on(this.button, 'click', this.onButtonClick.bind(this));
    }

    measure() {
      const previousLineClamp = this.description.style.webkitLineClamp;
      this.description.style.webkitLineClamp = 'unset';
      const fullHeight = this.description.scrollHeight;
      this.description.style.webkitLineClamp = previousLineClamp;

      this.style.setProperty('--expanded-height', `${fullHeight}px`);

      // Skip while expanded/collapsing: the line-clamp CSS doesn't apply in those states,
      // so offsetHeight would equal the full height instead of the true clamped height,
      // corrupting --collapsed-height for the next collapse.
      if (this.classList.contains('is-expanded') || this.classList.contains('is-collapsing')) return;

      const clampedHeight = this.description.offsetHeight;
      this.classList.toggle('is-truncated', fullHeight > clampedHeight + 2);
      this.style.setProperty('--collapsed-height', `${clampedHeight}px`);
    }

    onButtonClick() {
      const wasExpanded = this.classList.contains('is-expanded');
      const moreText = this.button.querySelector('.read-more-text');
      const lessText = this.button.querySelector('.read-less-text');

      if (wasExpanded) {
        this.collapse();
      }
      else {
        // Reversing mid-collapse: is-collapsing has to come off here rather than when the abandoned
        // transition settles. The two states are mutually exclusive, and the collapsing height wins
        // the cascade while both are set - leaving it on would finish the collapse before expanding.
        this.classList.remove('is-collapsing');
        this.classList.add('is-expanded');
      }

      if (moreText && lessText) {
        moreText.hidden = !wasExpanded;
        lessText.hidden = wasExpanded;
      }

      this.button.setAttribute('aria-expanded', String(!wasExpanded));
    }

    // is-collapsing withholds the line-clamp for the length of the max-height transition, so the
    // text slides up instead of snapping to N lines the moment the collapse starts. It has to come
    // back off afterwards, and waiting on the running animations is what makes that reliable: under
    // prefers-reduced-motion the CSS sets `transition: none`, so there is genuinely nothing to wait
    // for, where a transitionend listener would never fire at all and is-collapsing would stick -
    // clamp gone for good, measure() bailing out on every later resize.
    async collapse() {
      this.classList.add('is-collapsing');
      this.classList.remove('is-expanded');

      // Re-expanding mid-collapse cancels the transition, which rejects finished. allSettled counts
      // that as done, and the removal below is then a no-op: onButtonClick has already cleared the
      // class so the reversal could start animating immediately.
      await Promise.allSettled(this.description.getAnimations().map((animation) => animation.finished));

      this.classList.remove('is-collapsing');
    }
  });
}
